import React from 'react';

import {assert} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import DataFrameTableEditable from 'data-frames/editable-table/DataFrameTableEditable';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {omit} from 'util/omitKey';
import {JSONValue} from 'util/Types';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../StepConfigEditorContext';

export interface TableReferenceEditorProps {
  clientStateId: string;
  config: Array<Record<string, JSONValue>>;
  configShape: GetPipelineConfigSpecType<'tableReference'>;
  dataFrame: DataFrame;
  disabled?: boolean;
  disabledColumns?: Array<string>;
  hiddenColumns?: Array<string>;
  onChangeConfig: (config: Array<Record<string, JSONValue>>) => void;
  dataTestAttrs?: DataTestAttrs;
}

export function TableReferenceEditor({
  clientStateId,
  config,
  configShape,
  dataFrame,
  onChangeConfig,
  disabled,
  disabledColumns,
  hiddenColumns,
  dataTestAttrs,
}: TableReferenceEditorProps): JSX.Element {
  function handleUpdateDataFrameRows(newRows: Array<DataFrameRow>): void {
    // System columns are only added for the FE, and don't actually exist on the underlying data frame
    onChangeConfig(newRows.map((row) => omit(row, SystemColumnName.IS_EXCLUDED, SystemColumnName.ROW_ID)));
  }

  return (
    <DataFrameTableEditable
      originalDataFrame={dataFrame}
      dataFrameRowsOverride={config?.length > 0 ? config : dataFrame.rows}
      clientStateId={clientStateId}
      onUpdateDataFrameRows={handleUpdateDataFrameRows}
      disabled={disabled}
      disabledColumns={disabledColumns}
      hiddenColumns={hiddenColumns}
      dataTestAttrs={dataTestAttrs}
    />
  );
}

/** @package */
export type QueryingTableReferenceEditorProps = Omit<
  TableReferenceEditorProps,
  'clientStateId' | 'dataFrame'
>;

export default function QueryingTableReferenceEditor(props: QueryingTableReferenceEditorProps): JSX.Element {
  const {clientStateId, dataFramesInfos} = useStepConfigEditorContext();
  const {id: dataFrameId} = getDataFrameInfoOrEmptyInfo(
    dataFramesInfos,
    props.configShape.inputDataName,
    props.configShape.inputDataIndex
  );
  assert(dataFrameId != null, 'Using TableReferenceEditor in context without access to persisted data frame');

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame: persistedDataFrame}) => (
        <TableReferenceEditor
          {...props}
          clientStateId={`TableReferenceEditor-${clientStateId}-${dataFrameId}`}
          dataFrame={assertIsAccessible(persistedDataFrame).dataFrameJson}
        />
      )}
    </BenchlingQuery>
  );
}
